import { useEffect, useRef, useState } from 'react'
import { useT } from '../../i18n'
import {
  DATASETS,
  CITIES,
  MAYA_HOUSE,
  MA_PATH,
  MAP_WIDTH,
  MAP_HEIGHT,
  TONES,
  GLOW_CORAL,
  type Dataset,
} from '../../data/maMap'

const CYCLE = 4200 // ms per stat

function Defs() {
  return (
    <svg className="mgap__defs" width="0" height="0" aria-hidden="true">
      <defs>
        <linearGradient id="mgapFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#1d3352" />
          <stop offset="1" stopColor="#152740" />
        </linearGradient>
        <radialGradient id="mgapAura">
          <stop offset="0" className="mgap__aura0" />
          <stop offset="55%" className="mgap__aura1" />
          <stop offset="100%" className="mgap__aura2" />
        </radialGradient>
        <radialGradient id="mgapNode">
          <stop offset="0" className="mgap__nstop0" />
          <stop offset="35%" className="mgap__nstop1" />
          <stop offset="100%" className="mgap__nstop2" />
        </radialGradient>
        <radialGradient id="mgapAnchor">
          <stop offset="0" stopColor={GLOW_CORAL} stopOpacity="1" />
          <stop offset="40%" stopColor={GLOW_CORAL} stopOpacity="0.5" />
          <stop offset="100%" stopColor={GLOW_CORAL} stopOpacity="0" />
        </radialGradient>
        <filter id="mgapHalo" x="-40%" y="-40%" width="180%" height="180%">
          <feGaussianBlur stdDeviation="5" />
        </filter>
        <pattern id="mgapDotPat" width="11" height="11" patternUnits="userSpaceOnUse" patternTransform="translate(2 2)">
          <circle cx="1.4" cy="1.4" r="1.05" className="mgap__dots" />
        </pattern>
        <clipPath id="mgapClip">
          <path d={MA_PATH} />
        </clipPath>
      </defs>
    </svg>
  )
}

function MapViz({ dataset, drawn }: { dataset: Dataset; drawn: boolean }) {
  return (
    <svg className="mgap__svg" viewBox={`0 0 ${MAP_WIDTH} ${MAP_HEIGHT}`} role="img" aria-label={`Massachusetts: ${dataset.body}`}>
      <ellipse className="mgap__aura" cx="470" cy="300" rx="400" ry="250" fill="url(#mgapAura)" />
      <path d={MA_PATH} fill="url(#mgapFill)" />
      <path d={MA_PATH} fill="url(#mgapDotPat)" />
      <path d={MA_PATH} className="mgap__halo" fill="none" filter="url(#mgapHalo)" />
      <path d={MA_PATH} className={`mgap__stroke${drawn ? ' is-drawn' : ''}`} fill="none" pathLength={1} vectorEffect="non-scaling-stroke" />

      {/* weighted city lights for the active dataset */}
      <g key={dataset.id} className="mgap__nodes" clipPath="url(#mgapClip)">
        {dataset.nodes.map((n, i) => {
          const [x, y] = CITIES[n.at]
          const halo = 12 + n.weight * 44
          const core = 1.1 + n.weight * 2.4
          return (
            <g key={n.at} className="mgap__node" style={{ animationDelay: `${(i * 0.06).toFixed(2)}s`, transformOrigin: `${x}px ${y}px` }}>
              <circle className="mgap__nodehalo" cx={x} cy={y} r={halo} fill="url(#mgapNode)" style={{ transformOrigin: `${x}px ${y}px`, animationDelay: `${(i * 0.35).toFixed(2)}s` }} />
              <circle className="mgap__nodecore" cx={x} cy={y} r={core} />
            </g>
          )
        })}
      </g>

      {/* Maya House — the one solid coral anchor */}
      <g className="mgap__anchor">
        <circle cx={MAYA_HOUSE[0]} cy={MAYA_HOUSE[1]} r="13" fill="url(#mgapAnchor)" />
        <circle cx={MAYA_HOUSE[0]} cy={MAYA_HOUSE[1]} r="3.6" fill={GLOW_CORAL} />
        <text className="mgap__label" x={MAYA_HOUSE[0]} y={MAYA_HOUSE[1] + 20} textAnchor="middle">MAYA HOUSE</text>
      </g>
    </svg>
  )
}

export default function HousingGapMap() {
  const ref = useRef<HTMLDivElement>(null)
  const t = useT()
  const [ready, setReady] = useState(false)
  const [inView, setInView] = useState(false)
  const [paused, setPaused] = useState(false)
  const [index, setIndex] = useState(0)

  useEffect(() => setReady(true), [])

  useEffect(() => {
    if (typeof window === 'undefined' || !window.IntersectionObserver) return
    const el = ref.current
    if (!el) return
    const io = new IntersectionObserver((entries) => setInView(entries[0].isIntersecting), { threshold: 0.2 })
    io.observe(el)
    return () => io.disconnect()
  }, [])

  useEffect(() => {
    if (!inView || !ready || paused) return
    const t = window.setTimeout(() => setIndex((i) => (i + 1) % DATASETS.length), CYCLE)
    return () => window.clearTimeout(t)
  }, [inView, ready, paused, index])

  const active = DATASETS[index]
  const color = TONES[active.tone]

  const panel = (
    <div className="mgap__panel">
      {DATASETS.map((d, i) => (
        <div key={d.id} className={`mgap__stat${i === index ? ' is-active' : ''}`}>
          <div className="mgap__statNum" style={{ color: TONES[d.tone], textShadow: `0 0 42px ${TONES[d.tone]}73` }}>{d.stat}</div>
          <p className="mgap__statDesc">{t(d.body)}</p>
          <div className="mgap__statSrc">{d.source}</div>
        </div>
      ))}
      <div className="mgap__ticks">
        {DATASETS.map((d, i) => (
          <button
            key={d.id}
            type="button"
            aria-label={`Show ${d.stat}`}
            aria-current={i === index}
            className={`mgap__tick${i === index ? ' is-active' : ''}`}
            onClick={() => setIndex(i)}
          >
            <span className="mgap__tickbase" />
            {i === index && (
              <span
                key={index}
                className={`mgap__tickfill${paused || !inView ? ' is-paused' : ''}`}
                style={{ background: color, boxShadow: `0 0 12px ${color}b3` }}
              />
            )}
          </button>
        ))}
      </div>
    </div>
  )

  return (
    <div
      ref={ref}
      className={`mgap${ready ? ' mgap--ready' : ''}`}
      style={{ ['--mgap-tone' as string]: color }}
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <Defs />
      <div className="mgap__head">
        <div className="mgap__eyebrow">{t(`The problem`)}</div>
        <h2 className="mgap__title">{t(`The housing gap is not abstract. It is measured.`)}</h2>
      </div>

      {/* desktop: stat flashes left, glowing lit map right */}
      <div className="mgap__stage">
        {panel}
        <div className="mgap__mapwrap"><MapViz dataset={active} drawn={inView} /></div>
      </div>

      {/* mobile: map on top, stats listed */}
      <div className="mgap__stack">
        <div className="mgap__mapwrap"><MapViz dataset={active} drawn={inView} /></div>
        <div className="mgap__stackList">
          {DATASETS.map((d) => (
            <div key={d.id} className="mgap__stackItem">
              <div className="mgap__statNum" style={{ color: TONES[d.tone], textShadow: `0 0 30px ${TONES[d.tone]}55` }}>{d.stat}</div>
              <p className="mgap__statDesc">{t(d.body)}</p>
              <div className="mgap__statSrc">{d.source}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  )
}
